import { TokenPriceHistoryService } from './tokenPriceHistory';
import { BlockchainInfoAPI } from './blockchainInfoAPI';

// Types
export type MarketPhase = 'Bear Market' | 'Accumulation' | 'Markup' | 'Distribution';

export interface BitcoinDataPoint {
  date: string;              // Format ISO 8601
  timestamp: number;         // Unix timestamp
  price: number;            // Prix BTC en USD
  miningCost: number;       // Coût de minage moyen en USD/BTC
  miningCostMin: number;    // Coût minimum (grandes fermes optimisées)
  miningCostMax: number;    // Coût maximum (petits mineurs, machine solo)
  hashrate: number;         // Network hashrate (TH/s) - synthétique pour MVP
  difficulty: number;       // Network difficulty - synthétique pour MVP
  cycle: number;            // Numéro du cycle (1, 2, 3...)
  phase: MarketPhase;       // Phase actuelle du cycle
  fractalValue: number;     // Valeur normalisée pour overlay
  rainbowBands?: {          // Bandes Rainbow Chart (optionnel)
    band1: number;          // Bleu foncé - "Fire Sale"
    band3: number;          // Vert - "Accumulate"
    band5: number;          // Jaune - "Hold"
    band7: number;          // Orange - "FOMO"
    band9: number;          // Rouge max - "Maximum Bubble"
  };
}

export interface MiningCostConfig {
  electricityCost: number;  // USD/kWh (moyenne US: ~$0.12)
  efficiency: number;       // J/TH (ex: Antminer S19: 34.5 J/TH)
  poolFee: number;         // % (généralement 1-2%)
  operationalCosts: number; // USD/mois (cooling, maintenance)
}

export interface CycleMetadata {
  cycleNumber: number;
  startDate: Date;
  endDate: Date;
  bottomPrice: number;
  topPrice: number;
  halvingDate: Date;
  roi: number;              // Return on Investment du cycle
}

// Configuration par défaut pour le coût de minage (moyenne)
// Basé sur les moyennes de l'industrie du minage Bitcoin US (novembre 2025)
const DEFAULT_MINING_CONFIG: MiningCostConfig = {
  electricityCost: 0.07,    // $0.07/kWh (moyenne industrielle US, nov 2025)
  efficiency: 25,           // ~25 J/TH (machines récentes: S21, M60s, etc.)
  poolFee: 0.02,           // 2%
  operationalCosts: 1000    // $1000/mois
};

// Configuration optimisée (grandes fermes, meilleurs tarifs)
const OPTIMIZED_MINING_CONFIG: MiningCostConfig = {
  electricityCost: 0.05,    // $0.05/kWh (grandes fermes avec contrats négociés)
  efficiency: 22,           // ~22 J/TH (dernières machines S21 Pro)
  poolFee: 0.015,          // 1.5% (meilleurs pools)
  operationalCosts: 800     // $800/mois (économies d'échelle)
};

// Configuration individuelle (petits mineurs, style Grok)
const INDIVIDUAL_MINING_CONFIG: MiningCostConfig = {
  electricityCost: 0.0908,  // $0.0908/kWh (Texas industriel réel - Grok)
  efficiency: 28,           // ~28 J/TH (machines moins récentes en moyenne)
  poolFee: 0.02,           // 2%
  operationalCosts: 1200    // $1200/mois (sans économies d'échelle)
};

// Métadonnées des cycles historiques
const CYCLE_METADATA: CycleMetadata[] = [
  {
    cycleNumber: 1,
    startDate: new Date('2013-01-01'),
    endDate: new Date('2014-12-31'),
    bottomPrice: 13,
    topPrice: 1150,
    halvingDate: new Date('2012-11-28'),
    roi: 8746 // (1150-13)/13 * 100
  },
  {
    cycleNumber: 2,
    startDate: new Date('2015-01-01'),
    endDate: new Date('2018-12-31'),
    bottomPrice: 200,
    topPrice: 19500,
    halvingDate: new Date('2016-07-09'),
    roi: 9650
  },
  {
    cycleNumber: 3,
    startDate: new Date('2019-01-01'),
    endDate: new Date('2022-12-31'),
    bottomPrice: 3200,
    topPrice: 69000,
    halvingDate: new Date('2020-05-11'),
    roi: 2056
  },
  {
    cycleNumber: 4,
    startDate: new Date('2023-01-01'),
    endDate: new Date('2026-12-31'),
    bottomPrice: 15500,    // Bottom en novembre 2022
    topPrice: 110000,      // ATH historique du cycle (données en temps réel via API)
    halvingDate: new Date('2024-04-20'),
    roi: 610  // (110000-15500)/15500 * 100 ≈ 610%
  }
];

// Phase colors pour UI
export const PHASE_COLORS = {
  'Bear Market': '#ef4444',
  'Accumulation': '#fbbf24',
  'Markup': '#60a5fa',
  'Distribution': '#a78bfa'
};

// Durées de phases (en mois) dans un cycle de 48 mois
const PHASE_DURATIONS = {
  'Bear Market': 12,
  'Accumulation': 12,
  'Markup': 18,
  'Distribution': 6
};

/**
 * Bitcoin Cycles Data Service
 * Gère les données des cycles fractals Bitcoin avec cache réutilisé
 */
export const BitcoinCyclesDataService = {
  /**
   * Calcule les bandes Rainbow Chart (régression logarithmique)
   * Basé sur le modèle populaire de blockchaincenter.net
   */
  calculateRainbowBands(timestamp: number): {
    band1: number;
    band3: number;
    band5: number;
    band7: number;
    band9: number;
  } {
    // Nombre de jours depuis le Genesis Block (3 janvier 2009)
    const genesisTimestamp = new Date('2009-01-03').getTime();
    const daysSinceGenesis = (timestamp - genesisTimestamp) / (1000 * 60 * 60 * 24);

    // Régression logarithmique : log10(prix) = a + b * log10(jours)
    // Coefficients approximatifs basés sur le Rainbow Chart populaire
    const logDays = Math.log10(Math.max(daysSinceGenesis, 1));

    // Bande centrale (référence)
    const baseLogPrice = -17.01593313 + 5.84509376 * logDays;

    // Différents offsets pour créer les bandes (en log scale)
    // Plus l'offset est grand, plus la bande est haute (bulle territory)
    const offsets = {
      band1: -0.9,  // Bleu foncé - "Fire Sale" (très bas)
      band3: -0.3,  // Vert - "Accumulate" (bas)
      band5: 0,     // Jaune - "Hold" (milieu - référence)
      band7: 0.4,   // Orange - "FOMO" (haut)
      band9: 0.9    // Rouge maximum - "Maximum Bubble" (très haut)
    };

    return {
      band1: Math.pow(10, baseLogPrice + offsets.band1),
      band3: Math.pow(10, baseLogPrice + offsets.band3),
      band5: Math.pow(10, baseLogPrice + offsets.band5),
      band7: Math.pow(10, baseLogPrice + offsets.band7),
      band9: Math.pow(10, baseLogPrice + offsets.band9)
    };
  },

  /**
   * Récupère le block reward actuel en fonction du timestamp
   * Inclut une estimation des frais de transaction moyens
   */
  getCurrentBlockReward(timestamp: number): number {
    const halvings = [
      { date: new Date('2012-11-28'), baseReward: 25, avgFees: 0.05 },
      { date: new Date('2016-07-09'), baseReward: 12.5, avgFees: 0.1 },
      { date: new Date('2020-05-11'), baseReward: 6.25, avgFees: 0.2 },
      { date: new Date('2024-04-20'), baseReward: 3.125, avgFees: 0.3 }, // Frais augmentent avec halvings
      { date: new Date('2028-05-01'), baseReward: 1.5625, avgFees: 0.5 },
    ];

    for (let i = halvings.length - 1; i >= 0; i--) {
      if (timestamp >= halvings[i].date.getTime()) {
        // Retourner block reward + frais de transaction moyens
        return halvings[i].baseReward + halvings[i].avgFees;
      }
    }

    return 50.05; // Reward initial + frais
  },

  /**
   * Calcule le coût de minage du réseau Bitcoin
   * Formule corrigée basée sur le hashrate total du réseau
   */
  calculateMiningCost(
    difficulty: number,
    networkHashrate: number, // TH/s (hashrate total du réseau)
    timestamp: number,
    config: MiningCostConfig = DEFAULT_MINING_CONFIG
  ): number {
    const { electricityCost, efficiency, poolFee, operationalCosts } = config;

    // Nombre de blocs par jour (en moyenne 144)
    const blocksPerDay = 144;

    // Reward actuel
    const blockReward = this.getCurrentBlockReward(timestamp);

    // Total BTC minés par jour par le réseau
    const totalBTCPerDay = blocksPerDay * blockReward;

    // Puissance totale consommée par le réseau (Watts)
    // efficiency est en J/TH (Joules par Terahash)
    // networkHashrate est en TH/s
    // Puissance (W) = TH/s * J/TH = Joules/s = Watts
    const networkPowerWatts = networkHashrate * efficiency;

    // Energie totale consommée par jour (kWh)
    const networkEnergyPerDayKWh = (networkPowerWatts * 24) / 1000;

    // Coût énergétique pour miner 1 BTC
    const energyCostPerBTC = (networkEnergyPerDayKWh * electricityCost) / totalBTCPerDay;

    // Coût total incluant pool fees et coûts opérationnels
    // Les coûts opérationnels sont répartis sur tous les BTC minés dans le mois
    const operationalCostPerBTC = (operationalCosts * 30) / (totalBTCPerDay * 30);
    const totalCost = energyCostPerBTC * (1 + poolFee) + operationalCostPerBTC;

    return totalCost;
  },

  /**
   * Détermine la phase du marché en fonction de la position dans le cycle
   */
  getPhaseForProgress(progressInCycle: number): MarketPhase {
    // progressInCycle: 0-1 représentant la position dans le cycle de 48 mois
    const totalDuration = 48;
    const currentMonth = progressInCycle * totalDuration;

    if (currentMonth < 12) return 'Bear Market';
    if (currentMonth < 24) return 'Accumulation';
    if (currentMonth < 42) return 'Markup';
    return 'Distribution';
  },

  /**
   * Calcule la valeur fractale (hauteur de l'aire) pour une phase donnée
   */
  calculateFractalValue(
    phase: MarketPhase,
    progressInPhase: number, // 0-1 dans la phase
    cycleNumber: number
  ): number {
    const baseValues = {
      'Bear Market': 3,
      'Accumulation': 5,
      'Markup': 10,
      'Distribution': 5
    };

    // Courbe sinusoïdale pour transition douce
    const curve = Math.sin(progressInPhase * Math.PI);

    // Amplitude décroissante avec les cycles (diminishing returns)
    const amplitudeFactor = 1 - (cycleNumber * 0.05);

    return baseValues[phase] * curve * amplitudeFactor;
  },

  /**
   * Génère des données synthétiques pour hashrate et difficulty
   * Basé sur les tendances historiques réelles
   * NOTE: Ces valeurs sont approximatives et servent de fallback si l'API échoue
   */
  generateSyntheticMetrics(timestamp: number): { hashrate: number; difficulty: number } {
    // Croissance exponentielle du hashrate au fil du temps
    const daysSinceGenesis = (timestamp - new Date('2009-01-03').getTime()) / (1000 * 60 * 60 * 24);

    // Hashrate approximatif en TH/s avec croissance ralentie après 2021
    // Valeurs approximatives basées sur l'historique:
    // 2009: ~0.001 TH/s
    // 2013: ~10,000 TH/s (10 PH/s)
    // 2017: ~10,000,000 TH/s (10 EH/s)
    // 2021: ~150,000,000 TH/s (150 EH/s)
    // 2025: ~700,000,000 TH/s (700 EH/s)

    // Utiliser une croissance plus modérée après 2021 pour éviter des valeurs irréalistes
    const days2021 = (new Date('2021-01-01').getTime() - new Date('2009-01-03').getTime()) / (1000 * 60 * 60 * 24);

    let hashrate: number;
    if (daysSinceGenesis <= days2021) {
      // Croissance exponentielle rapide jusqu'en 2021
      hashrate = Math.exp(0.0033 * daysSinceGenesis);
    } else {
      // Après 2021, croissance linéaire plus modérée
      const hashrate2021 = Math.exp(0.0033 * days2021); // ~150M TH/s
      const daysAfter2021 = daysSinceGenesis - days2021;
      // Croissance d'environ 130M TH/s par an (pour atteindre ~700M en 2025)
      hashrate = hashrate2021 + (daysAfter2021 * 356000); // 356000 TH/s par jour = ~130M/an
    }

    // Difficulty approximative (relation empirique avec hashrate)
    // difficulty ≈ hashrate * 7.5e12 (ajustement pour correspondre aux valeurs réelles)
    const difficulty = hashrate * 7500000000000;

    return { hashrate, difficulty };
  },

  /**
   * Efface tous les caches Bitcoin (prix, hashrate, difficulty)
   */
  clearAllBitcoinCaches(): void {
    try {
      console.log('🗑️ Clearing all Bitcoin caches...');
      const keys = Object.keys(localStorage);
      let cleared = 0;

      keys.forEach(key => {
        // Clear Bitcoin price caches
        if (key.startsWith('token_price_history_bitcoin_')) {
          localStorage.removeItem(key);
          cleared++;
        }
        // Clear Blockchain.info caches
        if (key.startsWith('blockchain_info_')) {
          localStorage.removeItem(key);
          cleared++;
        }
      });

      console.log(`✅ Cleared ${cleared} cache entries`);
    } catch (error) {
      console.error('Error clearing caches:', error);
    }
  },

  /**
   * Récupère les données complètes des cycles Bitcoin
   * Réutilise le cache de TokenPriceHistoryService
   */
  async getBitcoinCyclesData(
    startDate: string = '2013-01-01',
    endDate: string = '2026-12-31',
    miningConfig: MiningCostConfig = DEFAULT_MINING_CONFIG,
    forceRefresh: boolean = false
  ): Promise<BitcoinDataPoint[]> {
    try {
      console.log('📊 Fetching Bitcoin cycles data...');

      // Calculer le nombre de jours jusqu'à AUJOURD'HUI (pas jusqu'à endDate future)
      const start = new Date(startDate);
      const now = new Date();
      const days = Math.ceil((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

      console.log(`📅 Fetching data from ${startDate} to today (${days} days)`);

      // Si forceRefresh, effacer tous les caches
      if (forceRefresh) {
        console.log('🔄 Force refresh requested, clearing all caches...');
        this.clearAllBitcoinCaches();
      }

      // Récupérer les prix Bitcoin via le cache existant
      console.log(`📡 Fetching ${days} days of Bitcoin price history...`);

      // Check if we have very old cached data and auto-refresh
      const cacheKey = `token_price_history_bitcoin_${days}d`;
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData && !forceRefresh) {
        try {
          const parsed = JSON.parse(cachedData);
          const cacheAge = Date.now() - parsed.lastUpdated;
          const ageHours = Math.round(cacheAge / 1000 / 60 / 60);
          const ageMinutes = Math.round(cacheAge / 1000 / 60);
          console.log(`📦 Cache age: ${ageHours}h ${ageMinutes % 60}min`);

          // Force refresh if cache is older than 3 hours (more aggressive refresh)
          if (cacheAge > 3 * 60 * 60 * 1000) {
            console.log(`🗑️ Cache too old (${ageHours}h), clearing to fetch fresh data...`);
            localStorage.removeItem(cacheKey);
          }
        } catch (e) {
          console.warn('Failed to check cache age:', e);
        }
      }

      let priceHistory = await TokenPriceHistoryService.getTokenPriceHistory('bitcoin', days);

      // Vérifier si on a des données
      if (!priceHistory || Object.keys(priceHistory).length === 0) {
        console.warn('⚠️ No price data returned from API (rate limit or error)');
        console.log('📊 Falling back to synthetic data...');
        return this.generateSyntheticCycleData(startDate, endDate, miningConfig);
      }

      // Check if we have recent data (within last 2 days)
      const timestamps = Object.keys(priceHistory).map(Number);

      if (timestamps.length === 0) {
        console.warn('⚠️ Empty price history, using synthetic data');
        console.log('📊 Falling back to synthetic data...');
        return this.generateSyntheticCycleData(startDate, endDate, miningConfig);
      }

      const latestTimestamp = Math.max(...timestamps);
      const latestDate = new Date(latestTimestamp);
      const daysSinceLatest = (Date.now() - latestTimestamp) / (1000 * 60 * 60 * 24);

      console.log(`📅 Latest data point: ${latestDate.toISOString().split('T')[0]} (${daysSinceLatest.toFixed(1)} days ago)`);

      // If latest data is older than 1 day, fetch recent data separately for better freshness
      if (daysSinceLatest > 1) {
        console.log(`⚠️ Data is outdated (${daysSinceLatest.toFixed(1)} days old), fetching last 7 days separately...`);
        try {
          const recentData = await TokenPriceHistoryService.getTokenPriceHistory('bitcoin', 7);

          if (recentData && Object.keys(recentData).length > 0) {
            // Merge recent data (overwrites old values if overlap)
            priceHistory = { ...priceHistory, ...recentData };
            console.log(`✅ Merged with ${Object.keys(recentData).length} recent data points`);
          }
        } catch (err) {
          console.warn('⚠️ Failed to fetch recent data, continuing with existing data');
        }
      }

      // Convertir en array et trier par timestamp
      const priceData = Object.entries(priceHistory)
        .map(([timestamp, price]) => ({
          timestamp: parseInt(timestamp),
          price
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

      console.log(`✅ Loaded ${priceData.length} Bitcoin price data points`);

      if (priceData.length > 0) {
        const lastPrice = priceData[priceData.length - 1];
        const lastDate = new Date(lastPrice.timestamp);
        console.log(`📅 Last price data: ${lastDate.toISOString().split('T')[0]} - $${lastPrice.price.toLocaleString()}`);
      }

      // Si pas assez de données réelles, compléter avec données synthétiques
      if (priceData.length === 0) {
        console.warn('⚠️ No real data available, using synthetic data for demo');
        console.log('📊 Falling back to synthetic data...');
        return this.generateSyntheticCycleData(startDate, endDate, miningConfig);
      }

      // Récupérer les données de hashrate et difficulty de Blockchain.info
      console.log('📡 Fetching hashrate and difficulty data from Blockchain.info...');
      let hashrateData, difficultyData;
      let useRealMetrics = true;

      try {
        const metrics = await BlockchainInfoAPI.getHashrateAndDifficulty('all');
        hashrateData = metrics.hashrate;
        difficultyData = metrics.difficulty;
        console.log(`✅ Loaded ${hashrateData.values.length} hashrate points and ${difficultyData.values.length} difficulty points`);
      } catch (error) {
        console.warn('⚠️ Failed to fetch real hashrate/difficulty, will use synthetic data');
        useRealMetrics = false;
      }

      // Transformer les données en BitcoinDataPoint
      const result: BitcoinDataPoint[] = priceData.map((point, index) => {
        const date = new Date(point.timestamp);
        const dateStr = date.toISOString().split('T')[0];

        // Déterminer le cycle actuel
        const cycle = CYCLE_METADATA.find(c =>
          date >= c.startDate && date <= c.endDate
        ) || CYCLE_METADATA[CYCLE_METADATA.length - 1];

        // Calculer la progression dans le cycle (0-1)
        const cycleDuration = cycle.endDate.getTime() - cycle.startDate.getTime();
        const progressInCycle = (date.getTime() - cycle.startDate.getTime()) / cycleDuration;

        // Déterminer la phase
        const phase = this.getPhaseForProgress(progressInCycle);

        // Calculer la progression dans la phase
        const phaseDuration = PHASE_DURATIONS[phase];
        const phaseStart = {
          'Bear Market': 0,
          'Accumulation': 12,
          'Markup': 24,
          'Distribution': 42
        }[phase];
        const currentMonth = progressInCycle * 48;
        const progressInPhase = (currentMonth - phaseStart) / phaseDuration;

        // Calculer la valeur fractale
        const fractalValue = this.calculateFractalValue(phase, progressInPhase, cycle.cycleNumber);

        // Obtenir métriques (réelles si disponibles, sinon synthétiques)
        let hashrate: number;
        let difficulty: number;

        if (useRealMetrics && hashrateData && difficultyData) {
          // Utiliser les vraies données de Blockchain.info
          const realHashrate = BlockchainInfoAPI.getHashrateAt(hashrateData, point.timestamp);
          const realDifficulty = BlockchainInfoAPI.getDifficultyAt(difficultyData, point.timestamp);

          if (realHashrate !== null && realDifficulty !== null) {
            hashrate = realHashrate;
            difficulty = realDifficulty;
          } else {
            // Fallback sur synthétique si interpolation impossible
            const synthetic = this.generateSyntheticMetrics(point.timestamp);
            hashrate = synthetic.hashrate;
            difficulty = synthetic.difficulty;
          }
        } else {
          // Utiliser métriques synthétiques
          const synthetic = this.generateSyntheticMetrics(point.timestamp);
          hashrate = synthetic.hashrate;
          difficulty = synthetic.difficulty;
        }

        // Calculer coût de minage avec 3 configurations (min, moyen, max)
        const miningCost = this.calculateMiningCost(
          difficulty,
          hashrate,
          point.timestamp,
          miningConfig
        );
        const miningCostMin = this.calculateMiningCost(
          difficulty,
          hashrate,
          point.timestamp,
          OPTIMIZED_MINING_CONFIG
        );
        const miningCostMax = this.calculateMiningCost(
          difficulty,
          hashrate,
          point.timestamp,
          INDIVIDUAL_MINING_CONFIG
        );

        // Log pour le dernier point pour débugger
        if (index === priceData.length - 1) {
          // Détails complets du calcul
          const blocksPerDay = 144;
          const blockReward = this.getCurrentBlockReward(point.timestamp);
          const totalBTCPerDay = blocksPerDay * blockReward;
          const networkPowerWatts = hashrate * miningConfig.efficiency;
          const networkEnergyPerDayKWh = (networkPowerWatts * 24) / 1000;
          const energyCostPerBTC = (networkEnergyPerDayKWh * miningConfig.electricityCost) / totalBTCPerDay;

          console.log(`🔍 Last data point debug:`, {
            date: dateStr,
            price: point.price,
            hashrate: hashrate.toLocaleString(),
            'hashrate (TH/s)': hashrate,
            difficulty: difficulty.toLocaleString(),
            miningCost: miningCost.toLocaleString(),
            blockReward,
            useRealMetrics,
            calculation: {
              'Network Power (Watts)': networkPowerWatts.toLocaleString(),
              'Energy Per Day (kWh)': networkEnergyPerDayKWh.toLocaleString(),
              'Energy Cost Per BTC': energyCostPerBTC.toLocaleString(),
              'Electricity ($/kWh)': miningConfig.electricityCost,
              'Efficiency (J/TH)': miningConfig.efficiency,
              'BTC per day': totalBTCPerDay
            }
          });
        }

        return {
          date: dateStr,
          timestamp: point.timestamp,
          price: point.price,
          miningCost: Math.max(miningCost, 0), // Coût moyen
          miningCostMin: Math.max(miningCostMin, 0), // Coût minimum (grandes fermes)
          miningCostMax: Math.max(miningCostMax, 0), // Coût maximum (petits mineurs)
          hashrate,
          difficulty,
          cycle: cycle.cycleNumber,
          phase,
          fractalValue,
          rainbowBands: this.calculateRainbowBands(point.timestamp)
        };
      });

      // Ajouter des projections futures (+1 an) pour visualiser les phases à venir
      const historicalDataCount = result.length; // Stocker le nombre de points historiques
      if (result.length > 0) {
        const lastPoint = result[result.length - 1];
        const lastTimestamp = lastPoint.timestamp;
        const oneYearFuture = lastTimestamp + 365 * 24 * 60 * 60 * 1000;
        const endTimestamp = new Date(endDate).getTime();
        const projectionEndTimestamp = Math.min(oneYearFuture, endTimestamp);

        console.log(`📈 Adding future projections from ${new Date(lastTimestamp).toISOString().split('T')[0]} to ${new Date(projectionEndTimestamp).toISOString().split('T')[0]}`);

        // Générer des points hebdomadaires pour la projection
        let projectionDate = new Date(lastTimestamp + 7 * 24 * 60 * 60 * 1000); // +1 semaine
        while (projectionDate.getTime() <= projectionEndTimestamp) {
          const timestamp = projectionDate.getTime();
          const dateStr = projectionDate.toISOString().split('T')[0];

          // Déterminer le cycle
          const cycle = CYCLE_METADATA.find(c =>
            projectionDate >= c.startDate && projectionDate <= c.endDate
          ) || CYCLE_METADATA[CYCLE_METADATA.length - 1];

          // Calculer la progression dans le cycle
          const cycleDuration = cycle.endDate.getTime() - cycle.startDate.getTime();
          const progressInCycle = (timestamp - cycle.startDate.getTime()) / cycleDuration;

          // Phase
          const phase = this.getPhaseForProgress(progressInCycle);

          // Prix projeté basé sur le pattern cyclique
          const priceRange = cycle.topPrice - cycle.bottomPrice;
          const cyclicValue = Math.sin(progressInCycle * Math.PI);
          const projectedPrice = cycle.bottomPrice + priceRange * cyclicValue;

          // Métriques synthétiques pour le futur
          const { hashrate, difficulty } = this.generateSyntheticMetrics(timestamp);
          const miningCost = this.calculateMiningCost(difficulty, hashrate, timestamp, miningConfig);
          const miningCostMin = this.calculateMiningCost(difficulty, hashrate, timestamp, OPTIMIZED_MINING_CONFIG);
          const miningCostMax = this.calculateMiningCost(difficulty, hashrate, timestamp, INDIVIDUAL_MINING_CONFIG);

          // Fractal value
          const phaseDuration = PHASE_DURATIONS[phase];
          const phaseStart = {
            'Bear Market': 0,
            'Accumulation': 12,
            'Markup': 24,
            'Distribution': 42
          }[phase];
          const currentMonth = progressInCycle * 48;
          const progressInPhase = (currentMonth - phaseStart) / phaseDuration;
          const fractalValue = this.calculateFractalValue(phase, progressInPhase, cycle.cycleNumber);

          result.push({
            date: dateStr,
            timestamp,
            price: projectedPrice,
            miningCost: Math.max(miningCost, 0),
            miningCostMin: Math.max(miningCostMin, 0),
            miningCostMax: Math.max(miningCostMax, 0),
            hashrate,
            difficulty,
            cycle: cycle.cycleNumber,
            phase,
            fractalValue,
            rainbowBands: this.calculateRainbowBands(timestamp)
          });

          // Avancer d'une semaine
          projectionDate = new Date(projectionDate.getTime() + 7 * 24 * 60 * 60 * 1000);
        }

        const projectedCount = result.length - historicalDataCount;
        console.log(`✅ Added ${projectedCount} future projection points`);
      }

      console.log(`✅ Bitcoin cycles data ready: ${result.length} points (${historicalDataCount} historical + ${result.length - historicalDataCount} projected)`);
      return result;

    } catch (error) {
      console.error('❌ Error fetching Bitcoin cycles data:', error);
      // Fallback sur données synthétiques
      console.log('📊 Falling back to synthetic data...');
      return this.generateSyntheticCycleData(startDate, endDate, miningConfig);
    }
  },

  /**
   * Génère des données synthétiques pour le MVP ou fallback
   */
  generateSyntheticCycleData(
    startDate: string,
    endDate: string,
    miningConfig: MiningCostConfig
  ): BitcoinDataPoint[] {
    console.log('🎲 Generating synthetic Bitcoin cycle data...');

    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();

    // Étendre jusqu'à +1 an dans le futur pour projections (mais pas au-delà de endDate)
    const oneYearFuture = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
    const actualEnd = end.getTime() < oneYearFuture.getTime() ? end : oneYearFuture;

    const data: BitcoinDataPoint[] = [];

    // Générer un point par semaine
    const currentDate = new Date(start);

    while (currentDate <= actualEnd) {
      const timestamp = currentDate.getTime();
      const dateStr = currentDate.toISOString().split('T')[0];

      // Déterminer le cycle
      const cycle = CYCLE_METADATA.find(c =>
        currentDate >= c.startDate && currentDate <= c.endDate
      ) || CYCLE_METADATA[CYCLE_METADATA.length - 1];

      // Progression dans le cycle
      const cycleDuration = cycle.endDate.getTime() - cycle.startDate.getTime();
      const progressInCycle = (timestamp - cycle.startDate.getTime()) / cycleDuration;

      // Phase
      const phase = this.getPhaseForProgress(progressInCycle);

      // Prix synthétique avec pattern cyclique
      const priceRange = cycle.topPrice - cycle.bottomPrice;
      const cyclicValue = Math.sin(progressInCycle * Math.PI);
      const price = cycle.bottomPrice + priceRange * cyclicValue;

      // Métriques synthétiques
      const { hashrate, difficulty } = this.generateSyntheticMetrics(timestamp);
      const miningCost = this.calculateMiningCost(difficulty, hashrate, timestamp, miningConfig);
      const miningCostMin = this.calculateMiningCost(difficulty, hashrate, timestamp, OPTIMIZED_MINING_CONFIG);
      const miningCostMax = this.calculateMiningCost(difficulty, hashrate, timestamp, INDIVIDUAL_MINING_CONFIG);

      // Fractal value
      const phaseDuration = PHASE_DURATIONS[phase];
      const phaseStart = {
        'Bear Market': 0,
        'Accumulation': 12,
        'Markup': 24,
        'Distribution': 42
      }[phase];
      const currentMonth = progressInCycle * 48;
      const progressInPhase = (currentMonth - phaseStart) / phaseDuration;
      const fractalValue = this.calculateFractalValue(phase, progressInPhase, cycle.cycleNumber);

      data.push({
        date: dateStr,
        timestamp,
        price,
        miningCost: Math.max(miningCost, 0),
        miningCostMin: Math.max(miningCostMin, 0),
        miningCostMax: Math.max(miningCostMax, 0),
        hashrate,
        difficulty,
        cycle: cycle.cycleNumber,
        phase,
        fractalValue,
        rainbowBands: this.calculateRainbowBands(timestamp)
      });

      // Avancer d'une semaine
      currentDate.setDate(currentDate.getDate() + 7);
    }

    console.log(`✅ Generated ${data.length} synthetic data points (${start.toISOString().split('T')[0]} to ${new Date(actualEnd).toISOString().split('T')[0]})`);

    // Log du dernier point pour vérifier
    if (data.length > 0) {
      const lastPoint = data[data.length - 1];
      console.log(`📅 Last synthetic point: ${lastPoint.date} - Price: $${lastPoint.price.toLocaleString()}, Mining: $${lastPoint.miningCost.toLocaleString()}`);
    }

    return data;
  },

  /**
   * Récupère les métadonnées des cycles
   */
  getCycleMetadata(): CycleMetadata[] {
    return CYCLE_METADATA;
  }
};
